import { Badge, Box, Button, Flex, Stack, Text } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import type { AgentToolDescriptor, RiskTier } from "./agentTypes";

export type PendingConfirmation = {
  label: string;
  riskTier: RiskTier;
  descriptor: AgentToolDescriptor;
  args: Record<string, unknown>;
};

type LookupEntity = (id: string) => Record<string, unknown> | undefined;

// A human label for an indexed entity, used to show what an id in the args refers to.
const ENTITY_LABEL_KEYS = ["supplierName", "title", "name", "subject", "referenceId"] as const;
const entityLabel = (entity: Record<string, unknown> | undefined): string => {
  if (!entity) {
    return "";
  }
  for (const key of ENTITY_LABEL_KEYS) {
    const value = entity[key];
    if (typeof value === "string" && value) {
      return value;
    }
  }
  return "";
};

// Human labels for body-field keys (the catalog carries raw OpenAPI property names). Literal
// t(...) so langen extracts them; unmapped fields fall back to the raw key.
const useFieldLabel = (): ((name: string) => string) => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    answer: t("Answer"),
    title: t("Title"),
    description: t("Description"),
    internalNote: t("Internal note"),
    winningBidIds: t("Winning bids"),
    estimatedValue: t("Estimated value"),
    contractNature: t("Contract type"),
  };
  return (name) => labels[name] ?? name;
};

const bodyParamOf = (descriptor: AgentToolDescriptor) => descriptor.params.find((param) => param.in === "body");

const hasValue = (value: unknown): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }
  if (typeof value === "string") {
    return value.trim().length > 0;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return true;
  }
  return value != null;
};

const formatScalar = (value: unknown): string => {
  if (value == null) {
    return "";
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    const amount = typeof obj.amount === "number" ? obj.amount : typeof obj.value === "number" ? obj.value : undefined;
    if (amount !== undefined && typeof obj.currency === "string") {
      return `${amount.toLocaleString("nb-NO")} ${obj.currency}`;
    }
    return JSON.stringify(value);
  }
  return String(value);
};

const ValueView = ({ value, lookupEntity }: { value: unknown; lookupEntity: LookupEntity }) => {
  if (Array.isArray(value)) {
    return (
      <Stack spacing="0.5">
        {value.map((item) => {
          const id = String(item);
          return (
            <Text key={id} fontSize="sm" fontWeight="semibold" color="smText">
              {entityLabel(lookupEntity(id)) || id}
            </Text>
          );
        })}
      </Stack>
    );
  }
  return (
    <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
      {formatScalar(value)}
    </Text>
  );
};

// Read-only confirmation: the user describes the action in chat, the agent turns it into
// structured args, and this just *displays* them for a yes/no. To change something the user
// chats again (the agent re-proposes) — no inline form editing. Catalog-driven: path params
// resolve (via the entity index) to a "Concerns" line, body fields are shown as a summary,
// required-but-missing fields block confirm. No per-endpoint code.
export const ConfirmationCard = ({
  pending,
  lookupEntity,
  onConfirm,
  onCancel,
  isBusy,
}: {
  pending: PendingConfirmation;
  lookupEntity: LookupEntity;
  onConfirm: (args: Record<string, unknown>) => void;
  onCancel: () => void;
  isBusy: boolean;
}) => {
  const { t } = useTranslation();
  const fieldLabel = useFieldLabel();

  const { descriptor, riskTier, args } = pending;
  const binding = riskTier === "binding_statutory";
  const body = bodyParamOf(descriptor);
  const payload = body && typeof args[body.name] === "object" ? (args[body.name] as Record<string, unknown>) : {};

  const concerns = descriptor.params
    .filter((param) => param.in === "path")
    .map((param) => entityLabel(lookupEntity(typeof args[param.name] === "string" ? (args[param.name] as string) : "")))
    .filter((label) => label.length > 0);

  const fields = body?.fields ?? [];
  const present = fields.filter((field) => hasValue(payload[field.name]));
  const missing = fields.filter((field) => field.required && !hasValue(payload[field.name]));

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="smBackground" p="4">
      <Flex align="center" gap="2" mb={binding ? "2" : "3"}>
        <Badge colorScheme={binding ? "red" : "orange"}>
          {binding ? t("Confirm — binding action") : t("Confirm action")}
        </Badge>
        <Text fontSize="sm" fontWeight="semibold" color="smText">
          {pending.label}
        </Text>
      </Flex>

      {binding && (
        <Text fontSize="xs" color="orange.600" mb="3">
          {t("Binding action — it may notify suppliers and start statutory deadlines.")}
        </Text>
      )}

      <Stack spacing="3" borderWidth="1px" borderColor="smBorder" borderRadius="md" bg="smBackgroundSecondary" p="3">
        {concerns.length > 0 && (
          <Box>
            <Text fontSize="xs" color="smTextDisabled" mb="1">
              {t("Concerns")}
            </Text>
            <Stack spacing="0.5">
              {concerns.map((label) => (
                <Text key={label} fontSize="sm" fontWeight="semibold" color="smText">
                  {label}
                </Text>
              ))}
            </Stack>
          </Box>
        )}

        {present.map((field) => (
          <Box key={field.name}>
            <Text fontSize="xs" color="smTextDisabled" mb="1">
              {fieldLabel(field.name)}
            </Text>
            <ValueView value={payload[field.name]} lookupEntity={lookupEntity} />
          </Box>
        ))}

        {present.length === 0 && concerns.length === 0 && (
          <Text fontSize="sm" color="smTextDisabled">
            {t("Confirm this action?")}
          </Text>
        )}
      </Stack>

      {missing.length > 0 && (
        <Text fontSize="xs" color="red.500" mt="2">
          {t("Missing")}: {missing.map((field) => fieldLabel(field.name)).join(", ")} —{" "}
          {t("tell me in the chat and I'll add it.")}
        </Text>
      )}

      <Flex justify="flex-end" gap="2" mt="4">
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={isBusy}>
          {t("Cancel")}
        </Button>
        <Button
          size="sm"
          bg="smPrimary"
          color="white"
          _hover={{ bg: "smPrimarySolid" }}
          onClick={() => onConfirm(args)}
          isLoading={isBusy}
          isDisabled={missing.length > 0}
        >
          {t("Confirm and execute")}
        </Button>
      </Flex>
    </Box>
  );
};
